/**
 * @fileoverview Maintenance Summary Component
 * @module MaintenanceSummary
 */

import React from 'react';
import { 
  Home, 
  Layers, 
  Wind, 
  Droplets, 
  Zap, 
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

interface CategorySummary {
  category: string;
  issueCount: number;
  estimatedCost: number;
  severity: string | null;
  keyFindings: string[];
}

interface MaintenanceSummaryProps {
  totalIssues: number;
  totalEstimatedCost: number;
  categorySummaries: CategorySummary[];
  overallSeverity: string;
  onGenerateArguments: () => void;
  isGenerating?: boolean;
}

const categoryIcons: Record<string, React.ReactNode> = {
  ROOF: <Home className="w-5 h-5" />,
  FOUNDATION: <Layers className="w-5 h-5" />,
  HVAC: <Wind className="w-5 h-5" />,
  PLUMBING: <Droplets className="w-5 h-5" />,
  ELECTRICAL: <Zap className="w-5 h-5" />,
};

const categoryColors: Record<string, string> = {
  ROOF: 'bg-red-50 text-red-600',
  FOUNDATION: 'bg-orange-50 text-orange-600',
  HVAC: 'bg-blue-50 text-blue-600',
  PLUMBING: 'bg-cyan-50 text-cyan-600',
  ELECTRICAL: 'bg-yellow-50 text-yellow-600',
};

const severityColors: Record<string, string> = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

const MaintenanceSummary: React.FC<MaintenanceSummaryProps> = ({
  totalIssues,
  totalEstimatedCost,
  categorySummaries,
  overallSeverity,
  onGenerateArguments,
  isGenerating = false,
}) => {
  const activeCategories = categorySummaries.filter(c => c.issueCount > 0);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900">Documentation Summary</h2>
        <p className="text-gray-600 mt-1">Review your documented issues before generating arguments</p>
      </div>

      {/* Overview Stats */}
      <div className="p-6 grid grid-cols-3 gap-6 border-b border-gray-100">
        <div className="text-center">
          <div className="text-4xl font-bold text-gray-900">{totalIssues}</div>
          <div className="text-gray-600 text-sm mt-1">Issues Documented</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold text-teal-600">{formatCurrency(totalEstimatedCost)}</div>
          <div className="text-gray-600 text-sm mt-1">Estimated Repair Cost</div>
        </div>
        <div className="text-center">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold capitalize ${severityColors[overallSeverity]}`}>
            {overallSeverity} Severity
          </span>
          <div className="text-gray-600 text-sm mt-1">Overall Impact</div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">By Category</h3>
        
        {activeCategories.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No issues documented yet.</p>
            <p className="text-gray-500 text-sm mt-1">Complete the maintenance checklists to document issues.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeCategories.map(category => (
              <div key={category.category} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${categoryColors[category.category]}`}>
                      {categoryIcons[category.category]}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 capitalize">
                        {category.category.toLowerCase()}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {category.issueCount} issue{category.issueCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(category.estimatedCost)}
                    </div>
                    {category.severity && (
                      <span className={`text-xs px-2 py-1 rounded-full ${severityColors[category.severity]}`}>
                        {category.severity}
                      </span>
                    )}
                  </div>
                </div>
                
                {category.keyFindings.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <ul className="text-sm text-gray-600 space-y-1">
                      {category.keyFindings.slice(0, 3).map((finding, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <span className="capitalize">{finding.replace(/_/g, ' ')}</span>
                        </li>
                      ))}
                      {category.keyFindings.length > 3 && (
                        <li className="text-gray-500">
                          +{category.keyFindings.length - 3} more findings
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate Arguments CTA */}
      <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-gray-100 rounded-b-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Ready to Generate Arguments?</h3>
            <p className="text-gray-600 text-sm mt-1">
              Use AI to create personalized talking points for your assessor meeting
            </p>
          </div>
          <button
            onClick={onGenerateArguments}
            disabled={totalIssues === 0 || isGenerating}
            className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>Generating...</>
            ) : (
              <>
                Generate Arguments
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSummary;











